import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EmailPreference } from 'shared'
import { Alert, SectionSpinner, Switch } from './ui'
import { api } from '../utils/api'
import { toErrorMessage } from '../lib/errors'

/**
 * Cosa questa persona vuole ricevere per email.
 *
 * Compaiono solo i tipi che l'admin ha acceso: offrire di disattivare
 * qualcosa che comunque non arriva confonde e basta. Se non è acceso niente,
 * la sezione dice che non c'è nulla da scegliere invece di mostrare un vuoto.
 */
export default function EmailPreferences() {
  const { t } = useTranslation()

  const [preferences, setPreferences] = useState<EmailPreference[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ data: EmailPreference[] }>('/api/me/email-preferences')
      .then((response) => setPreferences(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [])

  const toggle = async (preference: EmailPreference, enabled: boolean) => {
    setError(null)
    setPreferences((current) =>
      current.map((row) => (row.type === preference.type ? { ...row, enabled } : row))
    )

    try {
      await api.put(`/api/me/email-preferences/${preference.type}`, { enabled })
    } catch (err) {
      // Rimette l'interruttore dov'era: il server non ha accettato.
      setPreferences((current) =>
        current.map((row) => (row.type === preference.type ? preference : row))
      )
      setError(toErrorMessage(err) ?? 'error')
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <div>
      <h2 className="font-semibold text-gray-950 dark:text-white">{t('profile.emailTitle')}</h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {t('profile.emailDescription')}
      </p>

      {error && (
        <Alert variant="error" className="mt-4">
          {error}
        </Alert>
      )}

      {preferences.length === 0 ? (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">{t('profile.emailNone')}</p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100 dark:divide-gray-800">
          {preferences.map((preference) => (
            <li key={preference.type} className="flex items-center gap-4 py-3">
              <span className="min-w-0 flex-1 text-sm text-gray-900 dark:text-gray-50">
                {preference.label}
              </span>
              <Switch
                checked={preference.enabled}
                onCheckedChange={(enabled) => toggle(preference, enabled)}
                aria-label={preference.label}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
